import React, { useState, useEffect, useRef, useCallback } from 'react';

import css from './FireWeatherBanner.module.css';

const STYLE_MAP = {
  red: css.red,
  orange: css.orange,
  yellow: css.yellow,
};

const ACTION_ITEMS = [
  {
    label: 'Schedule a Consultation',
    href: '/s?pub_listingType=provider&pub_categoryLevel1=consultation',
  },
  {
    label: 'Get a Home Hardening Quote',
    href: '/s?pub_listingType=provider&pub_categoryLevel1=home-hardening',
  },
  {
    label: 'Learn About Insurance Discounts',
    href: '/resources',
  },
  {
    label: 'Plan Your Defensible Space',
    href: '/resources',
  },
];

const EXTERNAL_LINKS = [
  {
    label: 'View CAL FIRE Alerts',
    href: 'https://www.fire.ca.gov/incidents',
  },
  {
    label: 'View Full NWS Alert',
    href: null,
  },
];

const ExternalIcon = () => (
  <svg className={css.externalIcon} viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M4.5 1.5H2a.5.5 0 00-.5.5v8a.5.5 0 00.5.5h8a.5.5 0 00.5-.5V7.5M7 1.5h3.5V5M5.5 6.5l5-5" />
  </svg>
);

// ===== Tier 3: Burn Day Info Bar (shown when no fire alerts) =====

const BurnDayBar = () => {
  const [burnData, setBurnData] = useState(null);
  const [weather, setWeather] = useState(null);
  const [wfpi, setWfpi] = useState(null);

  useEffect(() => {
    const apiPort = process.env.REACT_APP_DEV_API_SERVER_PORT;
    const apiBase = apiPort ? `http://localhost:${apiPort}` : '';
    fetch(`${apiBase}/api/burn-day?_=${Date.now()}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data && data.today) {
          setBurnData(data);
        }
      })
      .catch(() => {});
    fetch(`${apiBase}/api/local-weather?_=${Date.now()}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data && data.temperature != null) {
          setWeather(data);
        }
      })
      .catch(() => {});
    fetch(`${apiBase}/api/wfpi?_=${Date.now()}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data && data.value != null) {
          setWfpi(data);
        }
      })
      .catch(() => {});
  }, []);

  if (!burnData && !weather && !wfpi) return null;

  const isBurnDay = burnData?.today === 'YES';

  const windDir = (deg) => {
    if (deg == null) return '';
    const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    return dirs[Math.round(deg / 45) % 8];
  };

  // Shorten the AQMD area name so the strip stays one line:
  // drop parentheticals and the trailing "- Burning is ..." explanation
  const shortArea = (area) =>
    (area || '')
      .replace(/\s*\(.*?\)\s*/g, ' ')
      .replace(/\s*-?\s*Burning\b.*$/i, '')
      .replace(/^Western\s+/, 'W. ')
      .replace(/^Eastern\s+/, 'E. ')
      .trim();

  const updatedAt = weather?.timestamp
    ? new Date(weather.timestamp).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    : null;

  const segments = [];
  if (weather?.temperature != null) {
    segments.push(
      <span key="temp" className={css.stripSegment}>
        <span className={css.stripTemp}>{weather.temperature}°</span>
        {weather.conditions ? ` ${weather.conditions}` : ''}
      </span>
    );
  }
  if (weather?.humidity != null) {
    segments.push(
      <span key="humidity" className={css.stripSegment}>Humidity {weather.humidity}%</span>
    );
  }
  if (weather?.windSpeed != null) {
    segments.push(
      <span key="wind" className={css.stripSegment}>
        Wind {weather.windSpeed} mph {windDir(weather.windDirection)}
      </span>
    );
  }
  if (burnData) {
    segments.push(
      <a
        key="burn"
        className={`${css.stripSegment} ${css.stripBurn}`}
        href={burnData.sourceUrl || 'https://www.myairdistrict.com/burn-day-status'}
        target="_blank"
        rel="noopener noreferrer"
        title="Nevada County AQMD burn day status — city burn bans apply · (530) 274-7928"
      >
        <span className={isBurnDay ? css.burnDotYes : css.burnDotNo} />
        {isBurnDay ? 'Burn Day' : 'No Burn Day'}
        {burnData.area ? ` — ${shortArea(burnData.area)}` : ''}
      </a>
    );
  }
  if (weather) {
    segments.push(
      <span
        key="sources"
        className={`${css.stripSegment} ${css.stripSource}`}
        title={updatedAt ? `Updated ${updatedAt}` : undefined}
      >
        via {weather.stationName || 'KBLU'}
      </span>
    );
  }

  return (
    <div className={css.burnDayRoot}>
      <div className={css.strip}>
        {segments.map((seg, i) => (
          <React.Fragment key={seg.key}>
            {i > 0 && <span className={css.stripSep} aria-hidden="true">·</span>}
            {seg}
          </React.Fragment>
        ))}
        {wfpi?.value != null && (
          <span className={`${css.stripSegment} ${css.stripWfpi}`}>
            <a
              className={css.stripLink}
              href="https://firedanger.cr.usgs.gov/apps/staticmaps"
              target="_blank"
              rel="noopener noreferrer"
              title="USGS Fire Danger Forecast — updated daily"
            >
              Wildland Fire Potential Index: {wfpi.value}
              <span className={css.wfpiScale}> (0–150)</span>
            </a>
            <a
              className={css.wfpiWhatsThis}
              href="https://www.usgs.gov/fire-danger-forecast/wildland-fire-potential-index-wfpi"
              target="_blank"
              rel="noopener noreferrer"
            >
              What&rsquo;s this?
            </a>
          </span>
        )}
      </div>
    </div>
  );
};

// ===== Main Banner =====

const FireWeatherBanner = () => {
  const [alert, setAlert] = useState(null);
  const [noAlerts, setNoAlerts] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
    const testParam = params?.get('firetest') === 'true' ? '&test=true&key=' + (params.get('key') || '') : '';
    const apiPort = process.env.REACT_APP_DEV_API_SERVER_PORT;
    const apiBase = apiPort ? `http://localhost:${apiPort}` : '';
    fetch(`${apiBase}/api/fire-weather?_=${Date.now()}${testParam}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.topAlert) {
          setAlert(data.topAlert);
        } else {
          setNoAlerts(true);
        }
      })
      .catch(() => setNoAlerts(true));
  }, []);

  // Close dropdown on outside click
  const handleClickOutside = useCallback((e) => {
    if (menuRef.current && !menuRef.current.contains(e.target)) {
      setMenuOpen(false);
    }
  }, []);

  useEffect(() => {
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [menuOpen, handleClickOutside]);

  // Tier 3: No fire alerts — show burn day info
  if (noAlerts) {
    return <BurnDayBar />;
  }

  // No data yet
  if (!alert) return null;

  const styleClass = STYLE_MAP[alert.style] || css.orange;

  const formatExpires = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <div className={`${css.root} ${styleClass}`}>
      <div className={css.content}>
        <div className={css.alertInfo}>
          <span className={css.icon}>
            {alert.style === 'red' ? '🔥' : alert.style === 'orange' ? '⚠️' : '🌡️'}
          </span>
          <div className={css.textContent}>
            <span className={css.eventName}>{alert.event}</span>
            {alert.description && (
              <span className={css.description}>{alert.description}</span>
            )}
            {alert.expires && (
              <span className={css.expires}>Until {formatExpires(alert.expires)}</span>
            )}
          </div>
        </div>
        <div className={css.ctaWrapper} ref={menuRef}>
          <button
            className={css.cta}
            onClick={() => setMenuOpen(!menuOpen)}
            type="button"
          >
            What can I do? {menuOpen ? '▲' : '▼'}
          </button>
          {menuOpen && (
            <div className={css.dropdown}>
              {ACTION_ITEMS.map((item, i) => (
                <a
                  key={i}
                  href={item.href}
                  className={css.dropdownItem}
                  onClick={() => setMenuOpen(false)}
                >
                  {item.label}
                </a>
              ))}
              <div className={css.dropdownDivider} />
              {EXTERNAL_LINKS.map((item, i) => {
                const href = item.href || alert.nwsUrl || 'https://www.weather.gov/';
                return (
                  <a
                    key={`ext-${i}`}
                    href={href}
                    className={css.dropdownItem}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setMenuOpen(false)}
                  >
                    {item.label}
                    <ExternalIcon />
                  </a>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FireWeatherBanner;
